import React, { useState, useRef, useEffect } from 'react';
import { X, Camera, RotateCcw, Share as ShareIcon } from 'lucide-react';
import BenderSummaryCard from './BenderSummaryCard';
import { shareImage } from '../utils/shareImage';

const OverlayCreator = ({ onClose }) => {
  const [step, setStep] = useState('input'); // input | preview
  const [formData, setFormData] = useState({
    drinks: '1',
    drinkType: '',
    duration: '1 HOUR'
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  
  const cardRef = useRef(null);

  // --- 1. DETECT PWA MODE ---
  useEffect(() => {
    const checkStandalone = () => {
      // Check for iOS "Standalone" or modern "Display Mode"
      const isiOSApp = window.navigator && window.navigator.standalone;
      const isMedia = window.matchMedia('(display-mode: standalone)').matches;
      const isNativeApp = window.Capacitor?.isNativePlatform?.();
      return !!(isiOSApp || isMedia || isNativeApp);
    };
    setIsStandalone(checkStandalone());
  }, []);

  const handleGenerate = async () => {
    if (!cardRef.current) return;
    setIsGenerating(true);
    
    // Small delay to ensure render
    await new Promise(r => setTimeout(r, 100));

    // Get Data URL string directly
    const dataUrl = await cardRef.current.generateImage();

    if (dataUrl) {
      setPreviewUrl(dataUrl);
      setStep('preview');
      setIsGenerating(false);
      // Auto-open the native share sheet with the freshly rendered card.
      handleShare(dataUrl);
    } else {
      setIsGenerating(false);
    }
  };

  const handleShare = async (url) => {
    const target = url || previewUrl;
    if (!target || isSharing) return;
    setIsSharing(true);
    try {
      await shareImage(target, 'benderquest-recap.png', 'My BenderQuest Recap');
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <div className="absolute inset-0 z-[3000] bg-black flex flex-col animate-in slide-in-from-bottom duration-300">
      
      {/* HEADER - CONDITIONAL PADDING */}
      {/* Standalone: pt-16 (Clears Notch) | Browser: p-4 (Standard) */}
      <div className={`flex justify-between items-center border-b border-white/10 bg-zinc-900 transition-all ${
          isStandalone ? 'pt-16 px-4 pb-4' : 'p-4'
      }`}>
        <h2 className="text-lg font-black italic text-white uppercase tracking-tighter">
            {step === 'input' ? 'CREATE OVERLAY' : 'YOUR SUMMARY'}
        </h2>
        <button onClick={onClose} className="text-zinc-500 hover:text-white">
            <X size={24} />
        </button>
      </div>

      {/* CONTENT AREA */}
      {/* Conditional top padding to balance with the header */}
      <div className={`flex-1 overflow-y-auto p-6 flex flex-col items-center ${
          isStandalone ? 'pt-8' : 'pt-4'
      }`}>
        
        {/* HIDDEN GENERATOR - Fixed Dimensions */}
        <div className="fixed left-[-9999px] top-0" style={{ width: 320, height: 600 }}>
             <BenderSummaryCard 
                ref={cardRef}
                drinks={formData.drinks}
                drinkType={formData.drinkType}
                duration={formData.duration}
             />
        </div>

        {step === 'input' && (
            <div className="w-full max-w-sm space-y-8 animate-in fade-in">
                
                {/* INPUTS */}
                <div className="space-y-6">
                    <div>
                        <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-2 block">Total Drinks</label>
                        <input 
                            type="number" 
                            value={formData.drinks}
                            onChange={e => setFormData({...formData, drinks: e.target.value})}
                            className="w-full bg-zinc-900 border border-zinc-700 rounded-xl p-4 text-white font-bold focus:border-brand-pink outline-none transition-colors text-lg"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-2 block">Drink of Choice</label>
                        <input 
                            type="text" 
                            placeholder="e.g. Tequila"
                            value={formData.drinkType}
                            onChange={e => setFormData({...formData, drinkType: e.target.value})}
                            className="w-full bg-zinc-900 border border-zinc-700 rounded-xl p-4 text-white font-bold focus:border-brand-teal outline-none transition-colors text-lg"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-2 block">Duration</label>
                        <input 
                            type="text" 
                            placeholder="e.g. 1 HOUR"
                            value={formData.duration}
                            onChange={e => setFormData({...formData, duration: e.target.value})}
                            className="w-full bg-zinc-900 border border-zinc-700 rounded-xl p-4 text-white font-bold focus:border-brand-pink outline-none transition-colors text-lg"
                        />
                    </div>
                </div>

                <button 
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className="w-full bg-white hover:bg-zinc-200 text-black font-black text-lg py-4 rounded-xl shadow-lg flex items-center justify-center gap-2 uppercase tracking-widest active:scale-95 transition-all mt-10"
                >
                    {isGenerating ? 'Rendering...' : <><Camera size={20} /> Generate Image</>}
                </button>
            </div>
        )}

        {step === 'preview' && previewUrl && (
            <div className="w-full max-w-sm flex flex-col items-center animate-in zoom-in-95 pt-4">
                
                <img
                    src={previewUrl} 
                    alt="Summary Preview" 
                    className="w-full h-auto max-w-[280px] drop-shadow-2xl mb-8 border border-white/10"
                    style={{ 
                        backgroundImage: 'linear-gradient(45deg, #18181b 25%, transparent 25%), linear-gradient(-45deg, #18181b 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #18181b 75%), linear-gradient(-45deg, transparent 75%, #18181b 75%)',
                        backgroundSize: '20px 20px',
                        backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
                    }}
                />

                <div className="w-full space-y-3">
                    <button
                        onClick={() => handleShare()}
                        disabled={isSharing}
                        className="w-full bg-white hover:bg-zinc-200 text-black font-black py-4 rounded-xl uppercase tracking-widest active:scale-95 transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-60"
                    >
                        <ShareIcon size={18} /> {isSharing ? 'Sharing…' : 'Share / Save'}
                    </button>
                    <button
                        onClick={() => setStep('input')}
                        className="w-full bg-zinc-800 text-zinc-400 hover:text-white font-bold py-4 rounded-xl uppercase tracking-widest active:scale-95 transition-all flex items-center justify-center gap-2"
                    >
                        <RotateCcw size={18} /> Edit / Redo
                    </button>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default OverlayCreator;
